import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles,
  ChevronDown,
  X,
  Plus,
  Minus,
  Loader2,
  Image as ImageIcon,
  Download,
  Copy,
  Trash2,
  Sliders,
  Upload,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ASPECT_RATIOS = [
  { value: "1:1", label: "1:1", icon: "■" },
  { value: "9:16", label: "9:16", icon: "▮" },
  { value: "16:9", label: "16:9", icon: "▬" },
  { value: "4:3", label: "4:3", icon: "▭" },
  { value: "3:4", label: "3:4", icon: "▯" },
];

function formatError(detail, fallback = "Something went wrong") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail.msg || detail.message || fallback;
  return String(detail);
}

function getCardWidth(aspect) {
  switch (aspect) {
    case "16:9": return "w-[320px]";
    case "9:16": return "w-[180px]";
    case "4:3":  return "w-[260px]";
    case "3:4":  return "w-[200px]";
    case "1:1":
    default:     return "w-[220px]";
  }
}

export default function ImageStudio() {
  const { creditsBalance, refreshCredits } = useAuth();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [resolution, setResolution] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [refPopoverOpen, setRefPopoverOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const modelDropdownRef = useRef(null);
  const settingsRef = useRef(null);

  // ─── Click-outside for dropdowns ──────────────────────────────────
  useEffect(() => {
    const onClickOutside = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) setShowModelDropdown(false);
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // ─── Load config + history ────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const cfg = await axios.get(`${API}/api/image-studio/config`, { withCredentials: true });
        setModels(cfg.data.models || []);
        if (cfg.data.models?.length) {
          const m = cfg.data.models[0];
          setSelectedModel(m);
          setAspectRatio(m.default_aspect || "1:1");
          setResolution(m.default_resolution || (m.supported_resolutions || [])[0] || null);
        }
      } catch (e) {
        toast.error("Could not load image studio config");
      }
      await refreshHistory();
    };
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/image-studio/history?limit=50`, { withCredentials: true });
      setHistory(res.data.jobs || []);
    } catch (e) {
      // silent
    }
  };

  // ─── Derived ──────────────────────────────────────────────────────
  const baseCost = selectedModel?.credits_per_image ?? 0;
  const multiplier = selectedModel?.resolution_multipliers?.[resolution] ?? 1;
  const perImageCost = baseCost * multiplier;
  const totalCost = perImageCost * quantity;
  const insufficientCredits = totalCost > (creditsBalance ?? 0);
  const promptInvalid = !prompt.trim();
  const canGenerate = selectedModel && !promptInvalid && !insufficientCredits && !generating;
  const maxRefs = selectedModel?.max_reference_count ?? 9;
  const supportedRes = selectedModel?.supported_resolutions || [];

  // Group models by provider for the picker
  const groupedModels = models.reduce((acc, m) => {
    const provider = m.provider || "Other";
    if (!acc[provider]) acc[provider] = [];
    acc[provider].push(m);
    return acc;
  }, {});

  // ─── Handlers ─────────────────────────────────────────────────────
  const handleModelChange = (model) => {
    setSelectedModel(model);
    setShowModelDropdown(false);
    // Reset aspect if current isn't supported
    if (!model.supported_aspects.includes(aspectRatio)) {
      setAspectRatio(model.default_aspect || model.supported_aspects[0]);
    }
    // Reset resolution
    if (!(model.supported_resolutions || []).includes(resolution)) {
      setResolution(model.default_resolution || (model.supported_resolutions || [])[0] || null);
    }
    // Trim refs to per-model cap
    const cap = model.max_reference_count ?? 9;
    if (!model.supports_reference) {
      clearAllReferences();
    } else if (referenceFiles.length > cap) {
      // Keep the most recent N
      const trimmed = referenceFiles.slice(-cap);
      const dropped = referenceFiles.length - cap;
      setReferenceFiles(trimmed);
      toast.message(`Switched to ${model.display_name} — kept the most recent ${cap} reference${cap > 1 ? "s" : ""} (dropped ${dropped})`);
    }
  };

  const handleQuantity = (delta) => {
    setQuantity((q) => Math.max(1, Math.min(4, q + delta)));
  };

  const MAX_PER_REF_BYTES = 10 * 1024 * 1024;
  const MAX_AGGREGATE_BYTES = 30 * 1024 * 1024;

  const handleFiles = (files) => {
    if (!files || !files.length) return;
    const cap = maxRefs;
    const remaining = cap - referenceFiles.length;
    if (remaining <= 0) {
      toast.error(`${selectedModel?.display_name} supports at most ${cap} reference image${cap > 1 ? "s" : ""}`);
      return;
    }
    const incoming = Array.from(files).slice(0, remaining);
    const aggregateSoFar = referenceFiles.reduce((s, r) => s + (r.file?.size || 0), 0);
    let aggregate = aggregateSoFar;
    const accepted = [];
    for (const file of incoming) {
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        toast.error(`${file.name}: PNG, JPEG, or WebP only`);
        continue;
      }
      if (file.size > MAX_PER_REF_BYTES) {
        toast.error(`${file.name}: each image must be under 10 MB`);
        continue;
      }
      aggregate += file.size;
      if (aggregate > MAX_AGGREGATE_BYTES) {
        toast.error(`Total reference image size exceeds 30 MB`);
        break;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    const readers = accepted.map((file) =>
      new Promise((resolve) => {
        const r = new FileReader();
        r.onload = (e) => resolve({ file, preview: e.target.result });
        r.readAsDataURL(file);
      })
    );
    Promise.all(readers).then((entries) => {
      setReferenceFiles((prev) => [...prev, ...entries]);
    });
  };

  const removeReference = (idx) => {
    setReferenceFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearAllReferences = () => {
    setReferenceFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ─── Generation + polling ─────────────────────────────────────────
  const pollJob = useCallback(async (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/image-studio/job/${jobId}`, { withCredentials: true });
        const job = res.data;
        setHistory((prev) => {
          const idx = prev.findIndex((j) => j.job_id === job.job_id);
          if (idx >= 0) {
            const copy = [...prev];
            copy[idx] = job;
            return copy;
          }
          return [job, ...prev];
        });
        if (["completed", "failed", "partial"].includes(job.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          if (job.status === "completed") toast.success(`Generated ${job.images.length} image${job.images.length > 1 ? "s" : ""}`);
          else if (job.status === "partial") toast.warning(`${job.images.length}/${job.quantity} image(s) generated — refunded credits for failed ones`);
          else toast.error("Generation failed — credits refunded");
          if (refreshCredits) refreshCredits();
        }
      } catch (e) {
        // silent — keep polling
      }
    }, 4000);
  }, [refreshCredits]);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const fd = new FormData();
      fd.append("model", selectedModel.model);
      fd.append("prompt", prompt.trim());
      fd.append("aspect_ratio", aspectRatio);
      if (resolution) fd.append("resolution", resolution);
      fd.append("quantity", String(quantity));
      referenceFiles.forEach(({ file }) => fd.append("reference_images", file));
      const res = await axios.post(`${API}/api/image-studio/generate`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.message("Generation started");
      const optimistic = {
        job_id: res.data.job_id,
        model: selectedModel.model,
        model_display: selectedModel.display_name,
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
        resolution,
        quantity,
        status: "pending",
        progress: 0,
        message: "Queued",
        images: [],
        failed: [],
        credits_charged: res.data.credits_charged,
        created_at: new Date().toISOString(),
      };
      setHistory((prev) => [optimistic, ...prev]);
      pollJob(res.data.job_id);
      if (refreshCredits) refreshCredits();
    } catch (e) {
      setGenerating(false);
      const msg = formatError(e?.response?.data?.detail, "Generation request failed");
      toast.error(msg);
    }
  };

  // ─── History actions ──────────────────────────────────────────────
  const deleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/api/image-studio/job/${jobId}`, { withCredentials: true });
      setHistory((prev) => prev.filter((j) => j.job_id !== jobId));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyPrompt = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Prompt copied");
  };

  const downloadImage = async (url, filename) => {
    try {
      const r = await fetch(url);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || "image.png";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(url, "_blank");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden" data-testid="image-studio-page">
      {/* Ambient gradient — matches Marketing Studio */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[hsl(11,100%,61%)] opacity-[0.08] blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-16 pb-24">
        {/* Hero */}
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-4" data-testid="is-subtitle">
            AI Image Generator
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-[1.1] tracking-tight">
            Turn Any Idea<br />Into a Stunning Image
          </h1>
        </div>

        {/* Main Creation Card */}
        <div className="relative z-[55] bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-5 space-y-4" data-testid="is-creation-card">
          {/* Row 1: Prompt textarea */}
          <div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              rows={2}
              className="w-full bg-transparent text-white placeholder-white/30 text-base resize-none outline-none min-h-[60px]"
              data-testid="image-studio-prompt-input"
            />
          </div>

          {/* Row 2: Center controls */}
          <div className="flex items-center justify-center gap-3">
            {/* Model Dropdown — grouped by provider */}
            <div className="relative z-[55]" ref={modelDropdownRef}>
              <button
                onClick={() => { setShowModelDropdown(!showModelDropdown); setShowSettings(false); }}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/15 transition-colors"
                data-testid="image-studio-model-picker"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                {selectedModel?.display_name || "Select Model"}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showModelDropdown ? "rotate-180" : ""}`} />
              </button>
              {showModelDropdown && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] py-1 max-h-[420px] overflow-y-auto" data-testid="image-studio-model-dropdown" onClick={(e) => e.stopPropagation()}>
                  {Object.entries(groupedModels).map(([provider, providerModels]) => (
                    <div key={provider} className="py-1">
                      <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-white/30">{provider}</p>
                      {providerModels.map((m) => (
                        <button
                          key={m.model}
                          onClick={() => handleModelChange(m)}
                          className={`w-full text-left px-4 py-2 flex flex-col hover:bg-white/5 transition-colors ${selectedModel?.model === m.model ? "bg-primary/10" : ""}`}
                          data-testid={`image-studio-model-option-${m.model}`}
                        >
                          <span className={`text-sm font-medium ${selectedModel?.model === m.model ? "text-primary" : "text-white"}`}>{m.display_name}</span>
                          <span className="text-[11px] text-white/40">
                            {m.credits_per_image} cr/img · {m.max_reference_count > 0 ? `${m.max_reference_count} ref${m.max_reference_count > 1 ? "s" : ""} max` : "no refs"}
                          </span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quantity Stepper Pill */}
            <div className="flex items-center rounded-full bg-white/10 p-0.5" data-testid="image-studio-quantity-stepper">
              <button
                onClick={() => handleQuantity(-1)}
                disabled={quantity <= 1}
                className="w-7 h-7 rounded-full grid place-items-center text-white/70 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="image-studio-quantity-decrement"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="px-2 text-sm tabular-nums text-white/80 min-w-[28px] text-center" data-testid="image-studio-quantity-value">{quantity}/4</span>
              <button
                onClick={() => handleQuantity(1)}
                disabled={quantity >= 4}
                className="w-7 h-7 rounded-full grid place-items-center text-white/70 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="image-studio-quantity-increment"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Settings (aspect ratio) */}
            <div className="relative z-[55]" ref={settingsRef}>
              <button
                onClick={() => { setShowSettings(!showSettings); setShowModelDropdown(false); }}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${showSettings ? "bg-primary/20 text-primary" : "bg-white/10 text-white/60 hover:bg-white/15"}`}
                data-testid="image-studio-settings-trigger"
              >
                <Sliders className="w-4 h-4" />
              </button>
              {showSettings && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] p-4 space-y-5" data-testid="image-studio-settings-panel" onClick={(e) => e.stopPropagation()}>
                  <div>
                    <p className="text-xs text-white/40 font-medium mb-2">Aspect Ratio</p>
                    <div className="flex flex-wrap gap-2" data-testid="image-studio-aspect-picker">
                      {ASPECT_RATIOS.filter((ar) => selectedModel?.supported_aspects?.includes(ar.value)).map((ar) => (
                        <button
                          key={ar.value}
                          onClick={(e) => { e.stopPropagation(); setAspectRatio(ar.value); }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            aspectRatio === ar.value
                              ? "bg-primary/20 text-primary border border-primary/30"
                              : "bg-white/5 text-white/60 border border-white/5 hover:bg-white/10"
                          }`}
                          data-testid={`image-studio-ar-${ar.value.replace(":", "-")}`}
                        >
                          <span className="text-[10px]">{ar.icon}</span>
                          {ar.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {supportedRes.length > 0 && (
                    <div>
                      <p className="text-xs text-white/40 font-medium mb-2">Resolution</p>
                      <div className="flex flex-wrap gap-2" data-testid="image-studio-resolution-picker">
                        {supportedRes.map((r) => {
                          const mult = selectedModel?.resolution_multipliers?.[r] ?? 1;
                          return (
                            <button
                              key={r}
                              onClick={(e) => { e.stopPropagation(); setResolution(r); }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                resolution === r
                                  ? "bg-primary/20 text-primary border border-primary/30"
                                  : "bg-white/5 text-white/60 border border-white/5 hover:bg-white/10"
                              }`}
                              data-testid={`image-studio-res-${r.replace(/[^a-zA-Z0-9]/g, "_")}`}
                            >
                              {r}
                              {mult !== 1 && <span className="text-[9px] opacity-70">×{mult}</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Reference image(s) + Generate */}
          <div className="flex items-center justify-center gap-3 pt-1">
            {/* Reference Images tile — shows latest + "+N more" badge with hover grid */}
            <div className="relative" onMouseLeave={() => setRefPopoverOpen(false)}>
              {referenceFiles.length > 0 && (
                <button
                  onClick={clearAllReferences}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 border border-white/20 text-white/70 hover:bg-red-600/80 hover:text-white hover:border-red-500 flex items-center justify-center z-20 transition-colors shadow-md"
                  data-testid="image-studio-ref-clear-all"
                  aria-label="Clear all references"
                  title="Remove all reference images"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => referenceFiles.length === 0 ? fileInputRef.current?.click() : setRefPopoverOpen((o) => !o)}
                onMouseEnter={() => referenceFiles.length > 0 && setRefPopoverOpen(true)}
                disabled={!selectedModel?.supports_reference}
                className="w-[72px] h-[88px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex flex-col items-center justify-center gap-1.5 overflow-hidden disabled:opacity-40 disabled:cursor-not-allowed relative"
                data-testid="image-studio-ref-upload"
                title={!selectedModel?.supports_reference ? "Model does not support reference" : referenceFiles.length === 0 ? `Upload reference images (up to ${selectedModel?.max_reference_count ?? 1})` : `${referenceFiles.length} reference image${referenceFiles.length > 1 ? "s" : ""}`}
              >
                {referenceFiles.length > 0 ? (
                  <>
                    <img src={referenceFiles[referenceFiles.length - 1].preview} alt="latest ref" className="w-full h-full object-cover" />
                    {referenceFiles.length > 1 && (
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-full bg-black/70 backdrop-blur-sm text-[10px] text-white font-semibold" data-testid="image-studio-ref-count-badge">
                        +{referenceFiles.length - 1}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                      <Upload className="w-4 h-4 text-white/40" />
                    </div>
                    <span className="text-[10px] text-white/40 font-medium uppercase tracking-wide">Reference</span>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                data-testid="image-studio-ref-input"
              />

              {/* Hover/click popover grid */}
              {refPopoverOpen && referenceFiles.length > 0 && (
                <div
                  className="absolute bottom-full left-0 mb-2 bg-slate-800 border border-white/10 rounded-xl shadow-2xl z-[60] p-3 w-[280px]"
                  onMouseEnter={() => setRefPopoverOpen(true)}
                  data-testid="image-studio-ref-popover"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-white/50 font-medium">{referenceFiles.length} / {maxRefs} references</p>
                    {referenceFiles.length < maxRefs && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-[10px] text-primary hover:text-primary/80 font-semibold uppercase tracking-wider"
                        data-testid="image-studio-ref-add-more"
                      >
                        + Add more
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {referenceFiles.map((rf, i) => (
                      <div key={i} className="relative group/ref aspect-square rounded-lg overflow-hidden border border-white/10 bg-white/5" data-testid={`image-studio-ref-thumb-${i}`}>
                        <img src={rf.preview} alt={`ref ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          onClick={(e) => { e.stopPropagation(); removeReference(i); if (referenceFiles.length === 1) setRefPopoverOpen(false); }}
                          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white/80 hover:bg-red-600 hover:text-white flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity"
                          data-testid={`image-studio-ref-remove-${i}`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              title={
                promptInvalid ? "Enter a prompt" :
                insufficientCredits ? `Need ${totalCost} credits, you have ${creditsBalance ?? 0}` :
                referenceFiles.length > 0 ? `${selectedModel?.display_name} · ${referenceFiles.length} ref${referenceFiles.length > 1 ? "s" : ""}` :
                selectedModel?.display_name
              }
              className={`h-[88px] px-6 rounded-xl bg-gradient-to-br from-primary to-[hsl(11,100%,50%)] text-white font-bold text-sm uppercase tracking-wider flex flex-col items-center justify-center gap-1 transition-opacity shadow-lg shadow-primary/20 ${!canGenerate ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
              data-testid="image-studio-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-[10px] font-medium">Processing</span>
                </>
              ) : (
                <>
                  <span className="text-xs font-black tracking-widest">Generate</span>
                  <span className="text-[10px] font-medium opacity-80">
                    {totalCost} cr
                    {resolution ? ` · ${resolution}` : ""}
                    {referenceFiles.length > 0 ? ` · ${referenceFiles.length} ref${referenceFiles.length > 1 ? "s" : ""}` : ""}
                  </span>
                </>
              )}
            </button>
          </div>

          {/* Insufficient-credit banner */}
          {insufficientCredits && selectedModel && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs"
              data-testid="image-studio-insufficient-credits"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" x2="12" y1="9" y2="13"/><line x1="12" x2="12.01" y1="17" y2="17"/></svg>
              <span>
                Need {totalCost} credits, you have {creditsBalance ?? 0}.{" "}
                <a href="/pricing" className="underline">Buy more credits</a>
              </span>
            </div>
          )}
        </div>

        {/* Image Grid — Processing card + history */}
        {(generating || history.length > 0) && (
          <div className="mt-10" data-testid="image-studio-history-section">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-widest mb-4">Your Generations</h2>
            {history.length === 0 ? (
              <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center" data-testid="image-studio-history-empty">
                <ImageIcon className="w-10 h-10 mx-auto mb-3 text-white/20" />
                <p className="text-white/40 text-sm">No generations yet. Compose a prompt above and hit Generate.</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-4 justify-center" data-testid="image-studio-history-grid">
                {history.flatMap((job) => {
                  // Processing or failed → one placeholder card per job
                  if (job.status === "pending" || job.status === "processing" || job.status === "failed") {
                    return [
                      <ImageHistoryCard
                        key={job.job_id}
                        job={job}
                        image={null}
                        imageIndex={0}
                        totalImages={job.quantity || 1}
                        onOpenImg={setLightboxImg}
                        onCopyPrompt={() => copyPrompt(job.prompt)}
                        onDelete={() => deleteJob(job.job_id)}
                        onDownload={downloadImage}
                      />,
                    ];
                  }
                  // Completed / partial → one card per successful image
                  const imgs = job.images || [];
                  if (imgs.length === 0) return [];
                  return imgs.map((img, i) => (
                    <ImageHistoryCard
                      key={`${job.job_id}-${img.index ?? i}`}
                      job={job}
                      image={img}
                      imageIndex={i}
                      totalImages={imgs.length}
                      onOpenImg={setLightboxImg}
                      onCopyPrompt={() => copyPrompt(job.prompt)}
                      onDelete={() => deleteJob(job.job_id)}
                      onDownload={downloadImage}
                    />
                  ));
                })}
              </div>
            )}
          </div>
        )}

        {/* Empty state when no history and not generating */}
        {!generating && history.length === 0 && (
          <div className="mt-10" data-testid="image-studio-history-section">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-widest mb-4">Your Generations</h2>
            <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center" data-testid="image-studio-history-empty">
              <ImageIcon className="w-10 h-10 mx-auto mb-3 text-white/20" />
              <p className="text-white/40 text-sm">No generations yet. Compose a prompt above and hit Generate.</p>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox modal — matches MS VideoModal style */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
          data-testid="image-studio-lightbox"
        >
          <div className="relative max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
              data-testid="image-studio-lightbox-close"
            >
              <X className="w-6 h-6" />
            </button>
            <img src={lightboxImg} alt="" className="w-full max-h-[85vh] object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── History card — one card per image (multi-image jobs produce N cards) ──
function ImageHistoryCard({ job, image, imageIndex, totalImages, onOpenImg, onCopyPrompt, onDelete, onDownload }) {
  const isProcessing = job.status === "pending" || job.status === "processing";
  const isFailed = job.status === "failed";
  const isPartial = job.status === "partial";
  const imgUrl = image?.url;
  const cardWidth = getCardWidth(job.aspect_ratio || "1:1");

  if (isFailed) {
    return (
      <div className={`${cardWidth} h-80 rounded-2xl overflow-hidden border border-white/10 bg-slate-900/60 shrink-0`} data-testid={`history-card-${job.job_id}`}>
        <div className="h-full flex flex-col items-center justify-center gap-2 p-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <X className="w-5 h-5 text-red-400" />
          </div>
          <p className="text-xs text-red-400 text-center">{formatError(job.error, "Generation failed")}</p>
          <p className="text-[10px] text-white/30">{new Date(job.created_at).toLocaleDateString()}</p>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className={`${cardWidth} h-80 rounded-2xl overflow-hidden border border-primary/20 bg-slate-900/80 relative shrink-0`} data-testid={`history-card-${job.job_id}`}>
        <div className="h-full relative">
          <div className="absolute top-0 left-0 right-0 h-24 overflow-hidden">
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[200%] h-24 rounded-[50%] bg-white/[0.03]" />
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-[180%] h-20 rounded-[50%] bg-white/[0.02]" />
          </div>
          <div className="relative p-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
              <Loader2 className="w-3.5 h-3.5 text-white/70 animate-spin" />
              <span className="text-xs text-white/70 font-medium">Processing</span>
            </div>
          </div>
          {job.progress > 0 && (
            <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
              <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-primary/50 rounded-full transition-all duration-1000" style={{ width: `${job.progress}%` }} />
              </div>
              <p className="text-[10px] text-white/30 mt-1.5 text-center">{job.message || "Generating..."}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!imgUrl) return null;

  const showPartial = isPartial && imageIndex === 0;
  const downloadName = totalImages > 1 ? `${job.job_id}-${imageIndex + 1}.png` : `${job.job_id}.png`;

  return (
    <div
      className={`${cardWidth} h-80 rounded-2xl overflow-hidden border border-white/10 bg-slate-900/60 relative group cursor-pointer shrink-0`}
      data-testid={`history-card-${job.job_id}-${imageIndex}`}
      onClick={() => onOpenImg(imgUrl)}
    >
      <div className="h-full bg-black">
        <img
          src={imgUrl}
          alt={job.prompt}
          className="w-full h-full object-cover"
        />
      </div>
      {/* Multi-image index pill (e.g., "2 / 4") */}
      {totalImages > 1 && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-[10px] text-white/80 font-medium" data-testid={`history-card-index-${job.job_id}-${imageIndex}`}>
          {imageIndex + 1} / {totalImages}
        </div>
      )}
      {/* Partial badge — shown on first tile only; positioned at top-right to avoid overlap with Delete (top-left) */}
      {showPartial && (
        <div className="absolute top-2 right-12 px-2 py-0.5 rounded-full bg-amber-500/80 text-[10px] text-white font-semibold uppercase tracking-wider" data-testid={`history-card-partial-${job.job_id}`}>
          Partial
        </div>
      )}
      {/* Download */}
      <button
        onClick={(e) => { e.stopPropagation(); onDownload(imgUrl, downloadName); }}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/80"
        data-testid={`history-card-download-${job.job_id}-${imageIndex}`}
      >
        <Download className="w-4 h-4" />
      </button>
      {/* Copy prompt */}
      <button
        onClick={(e) => { e.stopPropagation(); onCopyPrompt(); }}
        className="absolute bottom-12 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/20"
        data-testid={`history-card-copy-${job.job_id}-${imageIndex}`}
        title="Copy prompt"
      >
        <Copy className="w-4 h-4" />
      </button>
      {/* Delete — only on first tile to avoid accidentally deleting whole job from any image */}
      {imageIndex === 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600/80"
          data-testid={`history-card-delete-${job.job_id}`}
          title={totalImages > 1 ? "Delete entire job" : "Delete"}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-[10px] text-white/70 line-clamp-1">{job.prompt}</p>
        <p className="text-[10px] text-white/40">{job.model_display || job.model} · {job.aspect_ratio}</p>
      </div>
    </div>
  );
}
